import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';
import { Checkbox } from '../../../components/ui/Checkbox';

const RoomFormModal = ({ isOpen, onClose, onSave, room = null, categories }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    dimensions: '',
    backgroundImage: '',
    backgroundImageAlt: '',
    isDefault: false,
    lightingAdjustment: 0
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (room) {
      setFormData({
        name: room?.name || '',
        category: room?.category || '',
        dimensions: room?.dimensions || '',
        backgroundImage: room?.backgroundImage || '',
        backgroundImageAlt: room?.backgroundImageAlt || '',
        isDefault: room?.isDefault || false,
        lightingAdjustment: room?.lightingAdjustment || 0
      });
    } else {
      setFormData({
        name: '',
        category: '',
        dimensions: '',
        backgroundImage: '',
        backgroundImageAlt: '',
        isDefault: false,
        lightingAdjustment: 0
      });
    }
    setErrors({});
  }, [room, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Room name is required';
    }

    if (!formData?.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData?.backgroundImage) {
      newErrors.backgroundImage = 'Background image is required';
    }

    if (!formData?.backgroundImageAlt?.trim()) {
      newErrors.backgroundImageAlt = 'Image description is required for accessibility';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    setTimeout(() => {
      onSave({
        ...room,
        ...formData,
        id: room?.id || Date.now(),
        usageCount: room?.usageCount || 0,
        createdAt: room?.createdAt || new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      });
      setIsSaving(false);
      onClose();
    }, 800);
  };

  const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        backgroundImage: reader?.result
      }));
    };
    reader?.readAsDataURL(file);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">
            {room ? 'Edit Room' : 'Add New Room'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-150"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Room Name"
            type="text"
            placeholder="e.g., Modern Living Room"
            value={formData?.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
            error={errors?.name}
            required
          />

          <Select
            label="Category"
            placeholder="Select category"
            options={categories?.map(cat => ({ value: cat, label: cat }))}
            value={formData?.category}
            onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            error={errors?.category}
            required
          />

          <Input
            label="Dimensions (Optional)"
            type="text"
            placeholder="e.g., 20' x 15'"
            description="Specify room dimensions for customer reference"
            value={formData?.dimensions}
            onChange={(e) => setFormData(prev => ({ ...prev, dimensions: e?.target?.value }))}
          />

          <FileUpload
            label="Background Image"
            description="Upload high-resolution room background (max 5MB, JPG/PNG)"
            accept="image/jpeg,image/png"
            maxSize={5242880}
            onUpload={handleImageUpload}
            error={errors?.backgroundImage}
          />

          {formData?.backgroundImage && (
            <Input
              label="Image Description"
              type="text"
              placeholder="Describe the room background for accessibility"
              description="Detailed description of what the image shows"
              value={formData?.backgroundImageAlt}
              onChange={(e) => setFormData(prev => ({ ...prev, backgroundImageAlt: e?.target?.value }))}
              error={errors?.backgroundImageAlt}
              required
            />
          )}

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Lighting Adjustment
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="-50"
                max="50"
                value={formData?.lightingAdjustment}
                onChange={(e) => setFormData(prev => ({ ...prev, lightingAdjustment: parseInt(e?.target?.value) }))}
                className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm font-medium text-text-primary w-12 text-right">
                {formData?.lightingAdjustment}%
              </span>
            </div>
            <p className="text-xs text-text-secondary mt-1">
              Adjust lighting for consistent color representation
            </p>
          </div>

          <Checkbox
            label="Set as default room"
            description="This room will be pre-selected in the visualizer"
            checked={formData?.isDefault}
            onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e?.target?.checked }))}
          />

          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
              fullWidth
            >
              {room ? 'Update Room' : 'Add Room'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoomFormModal;